import { useEffect } from "react";

import { ActionType, createPoller, getPoller, PollerOptions, setPoller } from "@/Shared";

/**
 * =====================================================
 * useEntityPoller
 * =====================================================
 *
 * Custom React hook for managing polling of entity updates.
 * Handles:
 * - Conditional poller creation based on online status and permissions
 * - Subscriber counting for shared pollers
 * - Automatic cleanup when component unmounts
 *
 * Author: Samano CASTRE
 * Date: February 01, 2026
 */
export const useEntityPoller = (
    isOnline: boolean,
    canPoll: boolean,
    execute: (action: ActionType, payload?: any) => Promise<any>,
    options?: PollerOptions
) => {

    /** =====================================================
     * Poller setup
     * =====================================================
     *
     * Determines whether polling should be active and retrieves
     * an existing poller or creates a new one if needed.
     */
    const pollerKey = options?.pollerKey;
    const activePoller = canPoll && !!pollerKey;


    /** =====================================================
     * Effect: manage poller lifecycle
     * =====================================================
     *
     * - Starts the poller if online and allowed
     * - Reuses existing poller if available
     * - Increments subscriber count for shared pollers
     * - Decrements subscriber count on cleanup/unmount
     */
    useEffect(() => {
        if (!activePoller) return;

        let poller = getPoller(pollerKey);

        /** Initialize a new poller and start it */
        const initPoller = () => {
            poller = createPoller(
                async (lastPollingDate: string) => {
                    return await execute('fetchSinceDate', lastPollingDate);
                },
                options
            );
            setPoller(pollerKey, poller);
            poller.start();
        }

        if (isOnline) {
            poller ? poller.incrementSubscribers() : initPoller();
        }

        return () => {
            poller?.decrementSubscribers();
        };
    }, [isOnline, pollerKey, activePoller]);
}
